import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { AbstractDialogComponent, AuthStoreService, DialogNgbService } from '@motivforce/mx-library-angular';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { map } from 'rxjs/operators';
import { SprintActivity } from 'src/app/core/model/core/sprint-activity';
import { SubmitProof } from 'src/app/core/model/core/submit-proof';
import { ActivityStoreService } from 'src/app/core/store/activity-store.service';

function minMaxArrayLength(min: number, max: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormArray)) {
      return null;
    }

    const { length } = control;

    if (length < min) {
      return { minArrayLength: { requiredLength: min, actualLength: length } };
    }

    if (length > max) {
      return { maxArrayLength: { requiredLength: max, actualLength: length } };
    }

    return null;
  };
}

@Component({
  selector: 'app-submit-proof-dialog',
  templateUrl: './submit-proof-dialog.component.html',
  styleUrls: ['./submit-proof-dialog.component.scss'],
})
export class SubmitProofDialogComponent extends AbstractDialogComponent implements OnInit, AfterViewInit {
  @ViewChild('inputFile') inputFile: ElementRef;
  user$ = this.authStore.userSettings$.pipe(map((settings) => (settings ? settings.user : null)));

  submitProofForm: UntypedFormGroup = this.formBuilder.group({
    sprintActivityId: [null],
    activityDate: [null],
    BDFNumber: [null],
    resellerNames: [null],
    activityTitle: [{ value: null, disabled: true }],
    files: this.formBuilder.array([]),
  });

  activity: SprintActivity;
  currentFile: File | null;
  submitted = false;
  disableSubmitButton = false;
  showAllFields = false;
  BDFNumberRequired = false;
  showResellerName = false;
  dateRequired = false;
  resellerNameRequired = false;

  minDate: string;
  maxDate: string;

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: UntypedFormBuilder,
    private activityStore: ActivityStoreService,
    private authStore: AuthStoreService,
    private dialogService: DialogNgbService
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.inputs && this.inputs.length > 0) {
      const sprintActivity = this.inputs[0];
      if (sprintActivity) {
        this.activity = sprintActivity;
        this.submitProofForm.get('sprintActivityId')?.patchValue(sprintActivity.sprintActivityId);
        this.submitProofForm.get('activityTitle')?.patchValue(sprintActivity.title);
        this.setSubmitFormValidation(sprintActivity.sprintActivityId);
      } else {
        this.onClose();
      }
    }

    if (this.inputs && this.inputs.length > 1) {
      this.showAllFields = this.inputs[1];
    }

    /* uncomment/remove once the bussines rules are clear
    this.submitProofForm.get('fileUrl')?.valueChanges.subscribe((value) => {
      if (value && !this.showAllFields) {
        this.submitProofForm.get('BDFNumber')?.setValidators([]);
        this.submitProofForm.get('BDFNumber')?.updateValueAndValidity();
      }
    });


    this.submitProofForm.get('BDFNumber')?.valueChanges.subscribe((value) => {
      if (value && !this.showAllFields) {
        this.submitProofForm.get('fileUrl')?.setValidators([]);
        this.submitProofForm.get('fileUrl')?.updateValueAndValidity();
      } else if (!value && !this.showAllFields) {
        this.submitProofForm.get('fileUrl')?.setValidators([Validators.required]);
        this.submitProofForm.get('fileUrl')?.updateValueAndValidity();
      }
     });
    */
    const { start, end } = this.getQuarterStartEnd();
    this.minDate = this.formatDateLocal(start);
    this.maxDate = this.formatDateLocal(end);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      (document.activeElement as HTMLElement)?.blur();
    });
  }

  get files(): FormArray {
    return this.submitProofForm.get('files') as FormArray;
  }

  addFile(file: File, presignedUrl: { fileUrl: string; url: string; fileName: string }) {
    const fileGroup = this.formBuilder.group({
      presignedUrl: [presignedUrl],
      file: [file],
    });

    this.files.push(fileGroup);
  }

  removeFile(index: number) {
    this.files.removeAt(index);
  }

  addResellerName(name: string) {
    if (name && name.length > 0) {
      const control = this.submitProofForm.get('resellerNames');
      const currentNames = control?.value || [];
      control?.setValue([...currentNames, name]);
    }
  }

  removeResellerName(index: number) {
    const control = this.submitProofForm.get('resellerNames');
    const currentNames = control?.value || [];
    currentNames.splice(index, 1);
    control?.setValue([...currentNames]);
  }

  setSubmitFormValidation(id: number): void {
    const requiredFields = this.activityStore.activityRequirements?.find(
      (activity) => activity.sprintActivityId === id
    );
    this.clearFormValidation();
    if (requiredFields) {
      if (requiredFields.BDFNumberRequired) {
        // uncomment/remove once the bussines rules are clear
        // this.submitProofForm.get('BDFNumber')?.setValidators([Validators.required]);
        // this.submitProofForm.get('BDFNumber')?.updateValueAndValidity();
        this.BDFNumberRequired = true;
      }
      if (requiredFields.dateRequired) {
        this.submitProofForm.get('activityDate')?.setValidators([Validators.required]);
        this.submitProofForm.get('activityDate')?.updateValueAndValidity();
        this.dateRequired = true;
      }
      if (requiredFields.uploadProofRequired) {
        this.submitProofForm.get('files')?.setValidators([minMaxArrayLength(1, 3)]);
        this.submitProofForm.get('files')?.updateValueAndValidity();
      }
      if (requiredFields.resellerNameRequired) {
        this.resellerNameRequired = true;
      }
    }
  }

  clearFormValidation(): void {
    ['BDFNumber', 'activityDate', 'resellerName'].forEach((field) => {
      this.submitProofForm.get(field)?.clearValidators();
      this.submitProofForm.get(field)?.updateValueAndValidity();
    });
    this.BDFNumberRequired = false;
    this.dateRequired = false;
    this.resellerNameRequired = false;
  }

  async onSubmit(): Promise<void> {
    this.submitted = true;
    if (this.submitProofForm.valid) {
      this.disableSubmitButton = true;
      const uploadedFiles = await Promise.all(
        this.files.controls.map((control: AbstractControl) => {
          const fileGroup = control as UntypedFormGroup;
          const file = fileGroup.get('file')!.value as File;
          const presignedUrl = fileGroup.get('presignedUrl')!.value as {
            url: string;
            fileUrl: string;
            fileName: string;
          };
          return this.activityStore.uploadFile(presignedUrl, file);
        })
      );

      const allSucceeded = uploadedFiles.every((result) => result === true);

      if (!allSucceeded) {
        this.disableSubmitButton = false;
        this.files.clear();
        this.dialogService.openValidation(
          [
            'There was an error while uploading your files. Please try again or contact support if the problem persists.',
          ],
          'Important'
        );
        return;
      }

      const formValue = this.submitProofForm.value;
      const submitProof = {
        sprintActivityId: formValue.sprintActivityId,
        activityDate: formValue.activityDate,
        BDFNumber: formValue.BDFNumber,
        resellerNames: formValue.resellerNames,
        fileUrls: this.files.controls.map((control) => control.value.presignedUrl.fileUrl),
      } as SubmitProof;
      this.activityStore.submitProof(submitProof).then((data) => {
        if (data) {
          // Close current/previous dialog
          this.activeModal.close(true);

          this.dialogService.openFeatureDialog('submitProofThankYouDialog', [], {
            size: 'sm',
            windowClass: 'purple-modal-window',
          });
        }
      });
    }
  }

  onClose(): void {
    this.activeModal.close();
  }

  async handleFileUpload(event: any): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files) return;

    const sprintActivityId = this.submitProofForm.get('sprintActivityId')?.value;
    const files = Array.from(input.files);

    const uploadedUrls = await Promise.all(
      files.map((file) => this.activityStore.getPresignedUrlFile(sprintActivityId, file as File))
    );

    uploadedUrls.forEach((presignedUrl, index) => {
      if (presignedUrl) {
        this.addFile(files[index] as File, presignedUrl);
      }
    });
  }

  private getQuarterStartEnd(date: Date = new Date()) {
    const programCloseDate = new Date(2027, 0, 30);
    const year = date.getFullYear();
    const month = date.getMonth();

    const shiftedMonth = (month - 1 + 12) % 12;
    const quarterIndex = Math.floor(shiftedMonth / 3);
    const quarterStartMonth = (quarterIndex * 3 + 1) % 12;

    let startYear = year;
    if (month === 0) {
      startYear = year - 1;
    }
    const start = new Date(startYear, quarterStartMonth, 1);
    let end: Date;

    if (quarterIndex === 3) {
      end = programCloseDate;
    } else {
      const quarterEndMonth = (quarterStartMonth + 2) % 12;
      end = new Date(year, quarterEndMonth + 1, 0);
    }

    return { start, end };
  }

  private formatDateLocal(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  getIconName(file: File): string {
    if (file.type === 'application/pdf') {
      return '/assets/image/PDF.svg';
    }
    if (file.type.startsWith('image/')) {
      return '/assets/image/Image.svg';
    }
    return '/assets/image/File.svg';
  }
}
